import { useTranslations } from 'next-intl';
import type {
  Messages,
  NamespaceKeys,
  NestedKeyOf,
  NestedValueOf,
} from 'next-intl';

type FeatureMessages = Messages['features'];

/**
 * Keys under the `features` namespace whose value is a message *group* rather
 * than a single string — exactly the keys `raw()` is useful for.
 */
type FeatureGroupKey = NamespaceKeys<
  FeatureMessages,
  NestedKeyOf<FeatureMessages>
>;

/**
 * `t.raw()` returns whole message groups at runtime, but next-intl types its
 * key parameter as `MessageKeys` — leaf keys only — and its return value as
 * `any`. Call sites therefore had to write `t.raw('article' as any)`, which
 * both silenced the key check and discarded the type of what came back.
 *
 * This keeps the cast in one place, still validates the key against the
 * catalogue, and hands back the real type instead of `any`.
 */
export const useRawFeatureTranslations = <TKey extends FeatureGroupKey>(
  key: TKey,
): NestedValueOf<FeatureMessages, TKey> => {
  const t = useTranslations('features');

  return (t.raw as (rawKey: TKey) => NestedValueOf<FeatureMessages, TKey>)(key);
};
